import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon
} from '@heroicons/react/16/solid';
import {
  classSet,
  Icon
} from '@platform/ui-library';
import { clamp } from 'lodash';
import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import styles from './Carousel.module.scss';

const overflowTolerancePx = 1;

function getMaxScrollLeft(viewport: HTMLElement): number {
  return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
}

export type CarouselProps = {
  children: ReactNode;
  classNames?: {
    carousel?: string;
    track?: string;
    viewport?: string;
  };
  /** Changes (e.g. tab count) remeasure overflow. ResizeObserver misses some shrinks. */
  remeasureKey?: number | string;
  step?: number;
};

export function Carousel({
  children,
  classNames,
  remeasureKey,
  step = 400
}: CarouselProps): JSX.Element {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const [hasOverflow, setHasOverflow] = useState(false);
  const [previousDisabled, setPreviousDisabled] = useState(true);
  const [nextDisabled, setNextDisabled] = useState(true);

  const syncEdgeState = () => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const maxScrollLeft = getMaxScrollLeft(viewport);
    setHasOverflow(maxScrollLeft > overflowTolerancePx);
    setPreviousDisabled(viewport.scrollLeft <= overflowTolerancePx);
    setNextDisabled(viewport.scrollLeft >= maxScrollLeft - overflowTolerancePx);
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) {
      return;
    }

    syncEdgeState();

    viewport.addEventListener('scroll', syncEdgeState, { passive: true });

    const resizeObserver = (
      (typeof ResizeObserver === 'undefined') ? null : (
        new ResizeObserver(syncEdgeState)
      )
    );
    resizeObserver?.observe(viewport);
    resizeObserver?.observe(track);

    return () => {
      viewport.removeEventListener('scroll', syncEdgeState);
      resizeObserver?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    syncEdgeState();
    let nestedFrame = 0;
    const frame = requestAnimationFrame(() => {
      nestedFrame = requestAnimationFrame(() => {
        syncEdgeState();
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(nestedFrame);
    };
  }, [remeasureKey]);

  const moveBy = (direction: -1 | 1) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const scrollLeft = viewport.scrollLeft + direction * step;
    const target = clamp(scrollLeft, 0, getMaxScrollLeft(viewport));

    viewport.scrollTo({ left: target, behavior: 'smooth' });
  };

  return (
    <div
      className={classSet([styles.carousel, classNames?.carousel])}
      data-overflow={(hasOverflow) ? '' : undefined}
      data-part="carousel"
    >
      {(!hasOverflow) ? null : (
        <button
          data-part="previous-button"
          disabled={previousDisabled}
          onClick={() => moveBy(-1)}
          type="button"
        >
          <Icon icon={ChevronDoubleLeftIcon} />
        </button>
      )}
      <div
        className={classSet([
          styles.viewport,
          classNames?.viewport
        ])}
        data-part="viewport"
        ref={viewportRef}
      >
        <div
          className={classSet([
            styles.track,
            classNames?.track
          ])}
          data-part="track"
          ref={trackRef}
        >
          {children}
        </div>
      </div>
      {(!hasOverflow) ? null : (
        <button
          data-part="next-button"
          disabled={nextDisabled}
          onClick={() => moveBy(1)}
          type="button"
        >
          <Icon icon={ChevronDoubleRightIcon} />
        </button>
      )}
    </div>
  );
}
